import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { orderBy } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import type { ImportantDate, ImportantDateCategory, ImportantDateKind } from "../types";

const CATEGORY_COLORS = ["#6e2d49", "#c08a2e", "#2f6e52", "#2f5f8f", "#bf3a42", "#7a5c3e"];
const NONE = "__none__";
const KIND_LABELS: { value: ImportantDateKind; label: string }[] = [
  { value: "countdown", label: "Countdown" },
  { value: "countup", label: "Zähler (seit…)" },
];

function nextOccurrence(dateStr: string, repeatsYearly: boolean): Date {
  const base = new Date(dateStr + "T00:00");
  if (!repeatsYearly) return base;
  const now = new Date();
  const next = new Date(now.getFullYear(), base.getMonth(), base.getDate());
  if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
    next.setFullYear(now.getFullYear() + 1);
  }
  return next;
}

function daysUntil(d: Date): number {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function daysSince(dateStr: string): number {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(dateStr + "T00:00");
  return Math.max(0, Math.round((today.getTime() - start.getTime()) / 86400000));
}

export function ImportantDates() {
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const { items, loading, add, patch, remove } = useCoupleCollection<ImportantDate>(
    couple?.id ?? null,
    "importantDates",
    orderBy("date", "asc")
  );
  const { items: categories, add: addCategory } = useCoupleCollection<ImportantDateCategory>(
    couple?.id ?? null,
    "importantDateCategories",
    orderBy("createdAt", "asc")
  );
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [kind, setKind] = useState<ImportantDateKind>("countdown");
  const [repeatsYearly, setRepeatsYearly] = useState(true);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [showOnHome, setShowOnHome] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState(CATEGORY_COLORS[0]);

  // Arriving from the Neuigkeiten bell (?open=id) — open that entry's
  // edit sheet directly once it's actually loaded.
  const [searchParams] = useSearchParams();
  const [pendingOpenId, setPendingOpenId] = useState(() => searchParams.get("open"));
  useEffect(() => {
    if (!pendingOpenId) return;
    const match = items.find((d) => d.id === pendingOpenId);
    if (match) {
      openEdit(match);
      setPendingOpenId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingOpenId]);

  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  function toggleFilter(id: string) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  const filteredItems = useMemo(() => {
    if (activeFilters.size === 0) return items;
    return items.filter((d) => activeFilters.has(d.categoryId ?? NONE));
  }, [items, activeFilters]);

  function openAdd() {
    setEditingId(null);
    setTitle("");
    setDate("");
    setKind("countdown");
    setRepeatsYearly(true);
    setCategoryId(null);
    setShowOnHome(false);
    setNewCatOpen(false);
    setSheetOpen(true);
  }

  function openEdit(d: ImportantDate) {
    setEditingId(d.id);
    setTitle(d.title);
    setDate(d.date);
    setKind(d.kind ?? "countdown");
    setRepeatsYearly(d.repeatsYearly);
    setCategoryId(d.categoryId ?? null);
    setShowOnHome(d.showOnHome ?? false);
    setNewCatOpen(false);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setEditingId(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !title.trim() || !date || !user || !profile) return;
    setSubmitting(true);
    try {
      const data = {
        title: title.trim(),
        date,
        kind,
        repeatsYearly: kind === "countdown" ? repeatsYearly : false,
        categoryId,
        showOnHome,
      };
      if (editingId) {
        await patch(editingId, data);
      } else {
        await add({ ...data, createdByUid: user.uid, createdByName: profile.displayName });
      }
      closeSheet();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateCategory() {
    if (!newCatName.trim() || !user || !profile) return;
    await addCategory({
      name: newCatName.trim(),
      color: newCatColor,
      createdByUid: user.uid,
      createdByName: profile.displayName,
    });
    setNewCatName("");
    setNewCatOpen(false);
  }

  // Ongoing "countup" entries ("Zusammen seit…") lead the list — they're
  // not "upcoming" in the countdown sense, so sorting them by next-occurrence
  // alongside birthdays/anniversaries wouldn't mean anything.
  //
  // A non-repeating countdown whose date has passed (a one-off appointment,
  // or a birthday entered without "wiederholt sich jedes Jahr") would
  // otherwise show as "in -2 Tagen" forever — instead it's set aside into
  // its own collapsed "Ausgeblendet" section below, with the date it
  // expired on rather than a negative count. A repeating date never lands
  // here since its next occurrence always rolls forward to next year.
  const { active, expired } = useMemo(() => {
    const countups = filteredItems.filter((d) => d.kind === "countup");
    const countdowns = filteredItems.filter((d) => d.kind !== "countup");
    const upcoming: ImportantDate[] = [];
    const past: ImportantDate[] = [];
    for (const d of countdowns) {
      if (!d.repeatsYearly && daysUntil(nextOccurrence(d.date, d.repeatsYearly)) < 0) past.push(d);
      else upcoming.push(d);
    }
    upcoming.sort((a, b) => nextOccurrence(a.date, a.repeatsYearly).getTime() - nextOccurrence(b.date, b.repeatsYearly).getTime());
    past.sort((a, b) => b.date.localeCompare(a.date));
    return { active: [...countups, ...upcoming], expired: past };
  }, [filteredItems]);
  const [expiredOpen, setExpiredOpen] = useState(false);

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Wichtige Daten</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Jahrestage, Geburtstage & Co.</p>

      {categories.length > 0 && (
        <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => toggleFilter(c.id)}
              className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                activeFilters.has(c.id) ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
              }`}
              style={activeFilters.has(c.id) ? { backgroundColor: c.color, borderColor: c.color, color: "#fdf6f3" } : undefined}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: activeFilters.has(c.id) ? "#fdf6f3" : c.color }} />
              {c.name}
            </button>
          ))}
          <button
            onClick={() => toggleFilter(NONE)}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeFilters.has(NONE) ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
            }`}
            style={activeFilters.has(NONE) ? { backgroundColor: "#a6949b", borderColor: "#a6949b", color: "#fdf6f3" } : undefined}
          >
            Ohne Kategorie
          </button>
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : active.length === 0 && expired.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Noch keine wichtigen Daten.</p>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-3">
          {active.map((d) => {
            const cat = d.categoryId ? categoryById.get(d.categoryId) : null;
            const color = cat?.color ?? "#a6949b";
            const isCountup = d.kind === "countup";
            const next = isCountup ? null : nextOccurrence(d.date, d.repeatsYearly);
            const days = next ? daysUntil(next) : daysSince(d.date);
            return (
              <div
                key={d.id}
                className="relative flex flex-col justify-between overflow-hidden rounded-2xl p-4 pt-9 shadow-sm"
                style={{ backgroundColor: color, color: "#fdf6f3" }}
              >
                <div className="absolute right-2 top-2 flex gap-1">
                  <button onClick={() => openEdit(d)} aria-label="Bearbeiten" className="flex h-7 w-7 items-center justify-center rounded-full bg-black/10">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                      <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button onClick={() => remove(d.id)} aria-label="Löschen" className="flex h-7 w-7 items-center justify-center rounded-full bg-black/10">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                      <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-sm font-semibold leading-snug">
                    <span className="truncate">{d.title}</span>
                    {d.showOnHome && (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3 flex-shrink-0 opacity-90">
                        <path d="M12 2.5 14.8 9l6.7.6-5 4.5 1.5 6.6L12 17.4 6 20.7l1.5-6.6-5-4.5 6.7-.6L12 2.5Z" />
                      </svg>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs opacity-80">
                    {isCountup
                      ? new Date(d.date + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
                      : next!.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: d.repeatsYearly ? undefined : "numeric" })}
                  </p>
                </div>
                <p className="mt-3 text-lg font-bold leading-tight">
                  {isCountup ? `seit ${days} Tagen` : days === 0 ? "Heute" : days === 1 ? "Morgen" : `in ${days} Tagen`}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {expired.length > 0 && (
        <div className="mt-5">
          <button
            onClick={() => setExpiredOpen((v) => !v)}
            className="flex w-full items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`h-3 w-3 flex-shrink-0 transition-transform ${expiredOpen ? "rotate-90" : ""}`}
            >
              <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Ausgeblendet ({expired.length})
          </button>
          {expiredOpen && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {expired.map((d) => {
                const cat = d.categoryId ? categoryById.get(d.categoryId) : null;
                const color = cat?.color ?? "#a6949b";
                return (
                  <div
                    key={d.id}
                    className="relative flex flex-col justify-between overflow-hidden rounded-2xl p-4 pt-9 opacity-60 shadow-sm"
                    style={{ backgroundColor: color, color: "#fdf6f3" }}
                  >
                    <div className="absolute right-2 top-2 flex gap-1">
                      <button onClick={() => openEdit(d)} aria-label="Bearbeiten" className="flex h-7 w-7 items-center justify-center rounded-full bg-black/10">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                          <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button onClick={() => remove(d.id)} aria-label="Löschen" className="flex h-7 w-7 items-center justify-center rounded-full bg-black/10">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                          <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                    <p className="truncate text-sm font-semibold leading-snug">{d.title}</p>
                    <p className="mt-3 text-xs opacity-80">
                      Abgelaufen am {new Date(d.date + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Fab onClick={openAdd} label="Datum hinzufügen" />

      <Sheet open={sheetOpen} onClose={closeSheet} title={editingId ? "Datum bearbeiten" : "Neues Datum"}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="z.B. Jahrestag, Geburtstag von…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />

          <div>
            <p className="mb-1.5 text-xs font-medium text-ink-400 dark:text-night-faint">Art</p>
            <div className="flex gap-1.5">
              {KIND_LABELS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  className={`flex-1 rounded-xl border px-2 py-2 text-xs font-medium ${
                    kind === k.value ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                  }`}
                  style={kind === k.value ? { backgroundColor: "#6e2d49", borderColor: "#6e2d49", color: "#fdf6f3" } : undefined}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">{kind === "countup" ? "Start-Datum" : "Datum"}</p>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
          </div>

          {kind === "countdown" && (
            <div>
              <label className="flex items-center gap-2 text-sm text-ink-600 dark:text-night-muted">
                <input type="checkbox" checked={repeatsYearly} onChange={(e) => setRepeatsYearly(e.target.checked)} className="h-4 w-4" />
                Wiederholt sich jedes Jahr
              </label>
              <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
                {repeatsYearly
                  ? "Zählt jedes Jahr neu runter zum nächsten Datum — richtig für Jahrestage und Geburtstage."
                  : "Nur für einmalige Termine: Sobald das Datum vorbei ist, wandert der Eintrag unten zu „Ausgeblendet“ statt weiter zu zählen."}
              </p>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-medium text-ink-400 dark:text-night-faint">Kategorie (optional)</p>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoryId(categoryId === c.id ? null : c.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                    categoryId === c.id ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                  }`}
                  style={categoryId === c.id ? { backgroundColor: c.color, borderColor: c.color, color: "#fdf6f3" } : undefined}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: categoryId === c.id ? "#fdf6f3" : c.color }} />
                  {c.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setNewCatOpen((v) => !v)}
                className="rounded-full border border-dashed border-cream-200 px-3 py-1.5 text-xs font-medium text-ink-400 dark:border-night-border dark:text-night-faint"
              >
                + Neu
              </button>
            </div>

            {newCatOpen && (
              <div className="mt-2.5 space-y-2 rounded-xl bg-cream-100 p-3 dark:bg-night-surface-raised">
                <input
                  type="text"
                  placeholder="z.B. Geburtstag, Jahrestag, Sonstiges"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  className="h-10 w-full rounded-lg border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                <div className="flex items-center gap-2">
                  {CATEGORY_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewCatColor(c)}
                      aria-label={c}
                      className="h-6 w-6 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: c, boxShadow: newCatColor === c ? "0 0 0 2px white, 0 0 0 3.5px " + c : "none" }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={handleCreateCategory}
                    disabled={!newCatName.trim()}
                    className="ml-auto h-8 flex-shrink-0 rounded-lg bg-plum-700 px-3 text-xs font-semibold text-cream-50 disabled:opacity-50"
                  >
                    Erstellen
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-xl bg-cream-100 px-4 py-3 dark:bg-night-surface-raised">
            <div>
              <p className="text-sm font-medium text-ink-900 dark:text-night-text">Auf Startseite anzeigen</p>
              <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">Zeigt diesen Eintrag auf der Startseiten-Kachel.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowOnHome((v) => !v)}
              aria-pressed={showOnHome}
              className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${showOnHome ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${showOnHome ? "left-6" : "left-1"}`} />
            </button>
          </div>

          <button
            type="submit"
            disabled={!title.trim() || !date || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Datum hinzufügen"}
          </button>
        </form>
      </Sheet>
    </main>
  );
}
